import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { ReferenceBusinessId, ReferenceContactId } from "@sp-crm/core";
import {
    ActivityFeedRowBody,
    ActivityRowsList,
} from "components/details-page-v-next/activity-rows";
import {
    recentActivityItemDisplay,
    sortRecentActivityItems,
} from "components/details-page-v-next/recent-activities-feed";
import { ReferenceBusinessLink, ReferenceContactLink } from "components/ui/entity-link";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { ArrivingListItem, useArrivals } from "components/ui/list-motion";
import { AnimatePresence } from "framer-motion";
import React from "react";
import { AgendaRecentActivityItem } from "./marketing-plan-agenda";

const activityRowLink = (item: AgendaRecentActivityItem): React.ReactNode | null => {
    if (item.memberContact) {
        return (
            <ReferenceContactLink
                referenceContactId={item.memberContact.contactId as ReferenceContactId}
                name={item.memberContact.name}
            />
        );
    }
    if (item.taggedOnBusiness) {
        return (
            <ReferenceBusinessLink
                referenceBusinessId={
                    item.taggedOnBusiness.businessId as ReferenceBusinessId
                }
                name={item.taggedOnBusiness.name}
            />
        );
    }
    return null;
};

interface AgendaRecentActivityProps {
    items: AgendaRecentActivityItem[];
}

const INITIAL_VISIBLE_COUNT = 3;
const SHOW_MORE_INCREMENT = 10;

export const AgendaRecentActivity: React.FC<AgendaRecentActivityProps> = ({ items }) => {
    const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_COUNT);
    const itemIds = React.useMemo(() => items.map(item => item.id), [items]);
    const arrivals = useArrivals(itemIds);
    const sorted = sortRecentActivityItems(items);
    const visible = sorted.slice(0, visibleCount);
    const hasMore = sorted.length > visibleCount;
    const reachedEnd = !hasMore && visibleCount > INITIAL_VISIBLE_COUNT;
    return (
        <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                Recent Activities
            </p>
            {items.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">
                    No activity in the last 30 days.
                </p>
            ) : (
                <>
                    <ActivityRowsList className="mt-1" testId="agenda-recent-activity">
                        <AnimatePresence initial={false}>
                            {visible.map(item => (
                                <ArrivingListItem
                                    key={item.id}
                                    isNew={arrivals.has(item.id)}
                                    className="">
                                    <ActivityFeedRowBody
                                        display={{
                                            ...recentActivityItemDisplay(item),
                                            link: activityRowLink(item),
                                        }}
                                        actions={null}
                                    />
                                </ArrivingListItem>
                            ))}
                        </AnimatePresence>
                    </ActivityRowsList>
                    {hasMore ? (
                        <InteractiveTextLink
                            className="mt-1 inline-flex items-center gap-1 text-sm font-medium"
                            onClick={() =>
                                setVisibleCount(count => count + SHOW_MORE_INCREMENT)
                            }>
                            <ChevronDownIcon aria-hidden="true" className="h-4 w-4" />
                            Show more
                        </InteractiveTextLink>
                    ) : null}
                    {reachedEnd ? (
                        <p className="mt-1 text-sm text-gray-500">
                            No more activities to show.
                        </p>
                    ) : null}
                </>
            )}
        </div>
    );
};
